import { Check } from 'lucide-react'

interface CheckboxProps {
  checked: boolean
  onChange?: (checked: boolean) => void
  label?: string
  disabled?: boolean
  error?: boolean
  indeterminate?: boolean
  id?: string
}

/**
 * 16x16, background-body fill always, border shifts main -> hover when checked.
 * Never a coloured fill (design system, Checkbox visual spec).
 */
export function Checkbox({ checked, onChange, label, disabled, error, indeterminate, id }: CheckboxProps) {
  const borderColor = error ? '#F05042' : checked || indeterminate ? '#ABACAE' : '#4B5563'
  const box = (
    <span
      className="flex-shrink-0 flex items-center justify-center"
      style={{ width: 16, height: 16, backgroundColor: '#1A1D21', border: `0.5px solid ${borderColor}` }}
    >
      {indeterminate ? (
        <span style={{ width: 8, height: 1, backgroundColor: '#FFFFFF', display: 'block' }} />
      ) : checked ? (
        <Check size={10} color="#FFFFFF" strokeWidth={2.5} />
      ) : null}
    </span>
  )
  if (!label) {
    return (
      <button
        type="button"
        id={id}
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-disabled={disabled}
        onClick={() => !disabled && onChange?.(!checked)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
      >
        {box}
      </button>
    )
  }
  return (
    <button
      type="button"
      id={id}
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className="flex items-start text-left"
      style={{ gap: 10, background: 'none', border: 'none', padding: 0, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
    >
      {box}
      <span className="font-jakarta text-white" style={{ fontSize: 12, lineHeight: '16px', letterSpacing: '-0.12px' }}>
        {label}
      </span>
    </button>
  )
}

interface RadioProps {
  checked: boolean
  onChange?: () => void
  label?: string
  disabled?: boolean
  name?: string
}

/** 16x16 circle, background-body always, 8px white dot when selected. */
export function Radio({ checked, onChange, label, disabled, name }: RadioProps) {
  const circle = (
    <span
      className="flex-shrink-0 flex items-center justify-center"
      style={{
        width: 16, height: 16, borderRadius: '50%', backgroundColor: '#1A1D21',
        border: `0.5px solid ${checked ? '#ABACAE' : '#4B5563'}`, opacity: disabled ? 0.5 : 1,
      }}
    >
      {checked && <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#FFFFFF' }} />}
    </span>
  )
  return (
    <button
      type="button"
      role="radio"
      name={name}
      aria-checked={checked}
      aria-disabled={disabled}
      onClick={() => !disabled && onChange?.()}
      className="flex items-center text-left"
      style={{ gap: 10, background: 'none', border: 'none', padding: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      {circle}
      {label && (
        <span className="font-jakarta text-white" style={{ fontSize: 12, lineHeight: '16px', letterSpacing: '-0.12px' }}>
          {label}
        </span>
      )}
    </button>
  )
}

interface RadioGroupProps {
  options: { value: string; label: string }[]
  value: string | undefined
  onChange: (value: string) => void
  label?: string
  error?: string
  direction?: 'row' | 'column'
  disabled?: boolean
}

export function RadioGroup({ options, value, onChange, label, error, direction = 'column', disabled }: RadioGroupProps) {
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      {label && (
        <span className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
          {label}
        </span>
      )}
      <div className="flex" style={{ gap: direction === 'row' ? 20 : 10, flexDirection: direction }} role="radiogroup">
        {options.map((o) => (
          <Radio key={o.value} checked={value === o.value} onChange={() => onChange(o.value)} label={o.label} disabled={disabled} />
        ))}
      </div>
      {error && (
        <span className="font-mulish" style={{ fontSize: 10, lineHeight: '12px', color: '#F05042' }}>{error}</span>
      )}
    </div>
  )
}
